
import { Component, Prop, Vue } from 'vue-property-decorator';
import Icon from '@/layouts/Icon.vue';
import OrderApiConfig from '@/api/OrderApiConfig';
import {
  JpCoBenefitoneBsdpBffPresentationResponseCafeGetResponse,
  JpCoBenefitoneBsdpBffPresentationResponseCafeGetResponseCafeMenuInfo,
  DPApi,
  JpCoBenefitoneBsSubsidyDtoErrorData,
  JpCoBenefitoneBsCafeDtoInlineResponse400,
  PutCafeRequest,
} from '@/openapi/order';
import store from '@/store';
import ErrorForApiComponent from '@/components/ErrorForApiComponent.vue';
import EnvRepository from '@/repository/EnvRepository';
import ApiService from '@/services/ApiService';
import ConfirmModal from '@/modals/ConfirmModal.vue';

type UsePointType = 'Point' | 'Cash';
type UsePointErrorType = 'NoError' | 'Blank' | 'NotPositive' | 'NotNum' | 'ExceedPoint' | 'ExceedCash' | 'Over';

@Component({
  components: {
    ErrorForApiComponent,
    Icon,
    ConfirmModal,
  },
  filters: {
    comma(value?: number) {
      if (!value) return 0;
      return value.toLocaleString();
    },
  },
})
export default class Cafe01View extends Vue {
  //* ***********************************************************************************/
  // Props
  //* ***********************************************************************************/
  @Prop() public orderSite: number;

  //* ***********************************************************************************/
  // Data
  //* ***********************************************************************************/
  cafeGetResponse: JpCoBenefitoneBsdpBffPresentationResponseCafeGetResponse = {};

  commonErrorResponse: JpCoBenefitoneBsSubsidyDtoErrorData = { errorCode: '', errorDetail: '' };

  cafeErrorResponse: JpCoBenefitoneBsCafeDtoInlineResponse400 = {};

  selectedCafeMenuInfo: JpCoBenefitoneBsdpBffPresentationResponseCafeGetResponseCafeMenuInfo = {};

  form: {
    usePoint: {
      value: number | '';
      type: UsePointType;
      errorType: UsePointErrorType;
    };
    checkedAgree1: boolean;
    checkedAgree2: boolean;
  } = {
    usePoint: {
      value: 0,
      type: 'Point',
      errorType: 'NoError',
    },
    checkedAgree1: false,
    checkedAgree2: false,
  };

  optionUsePoint: Array<{ value: UsePointType; label: string }> = [
    { value: 'Point', label: 'ポイント' },
    { value: 'Cash', label: '円' },
  ];

  useCafeFlag = true;

  disabledInputUsePoint = false;

  showConfirmModal = false;

  //* ***********************************************************************************/
  // Computed
  //* ***********************************************************************************/
  get getErrorBlank(): string {
    return EnvRepository.getEnv().VUE_APP_CAFE01_ERR_BLANK;
  }

  get getErrorNotPositive(): string {
    return EnvRepository.getEnv().VUE_APP_CAFE01_ERR_NEGATIVE;
  }

  get getErrorNum(): string {
    return EnvRepository.getEnv().VUE_APP_CAFE01_ERR_NUM;
  }

  get getErrorExceedPoint(): string {
    return EnvRepository.getEnv().VUE_APP_CAFE01_ERR_EXCEED_POINT;
  }

  get getErrorExceedCash(): string {
    return EnvRepository.getEnv().VUE_APP_CAFE01_ERR_EXCEED_CASH;
  }

  get getErrorOver(): string {
    return EnvRepository.getEnv().VUE_APP_CAFE01_ERR_OVER || EnvRepository.getEnv().VUE_APP_BENP01_ERR_OVER;
  }

  get isDisabledCafeMenuRadio(): boolean {
    const hasUpperLimitPoints = this.cafeGetResponse.cafeMenuInfo?.filter((item) => item.upperLimitPoint && item.upperLimitPoint > 0);
    return !(hasUpperLimitPoints && hasUpperLimitPoints.length);
  }

  //* ***********************************************************************************/
  // 初期表示
  //* ***********************************************************************************/
  mounted(): void {
    if (this.orderSite === 4) {
      this.doApiFetCafeFellowTravelers();
    } else {
      this.doApiGetCafe();
    }
  }

  doApiGetCafe(): void {
    const config = OrderApiConfig.newInstance();
    const apiOrder = new DPApi(config);
    store.commit('setIsLoading', true);

    apiOrder
      .getCafe()
      .then((res) => {
        this.cafeGetResponse = res;

        // restore radio.
        if (res.cafeReservation && res.cafeReservation.cafeMenuNo && res.cafeMenuInfo) {
          const menuNo = res.cafeReservation.cafeMenuNo;
          const cafeMenuInfo = res.cafeMenuInfo.find((info) => info.cafeMenuNo === menuNo);
          if (cafeMenuInfo) {
            this.changeCafeMenuInfo(cafeMenuInfo);
          }
        }

        // init radio if needed.
        if (Object.keys(this.selectedCafeMenuInfo).length === 0 && res.cafeMenuInfo && res.cafeMenuInfo.length > 0) {
          const [first] = res.cafeMenuInfo;
          this.changeCafeMenuInfo(first);
        }

        // restore points.
        if (res.cafeReservation) {
          this.form.usePoint = {
            value: Number(res.cafeReservation.applyValue) ?? 0,
            type: res.cafeReservation.applyUnit === '1' ? 'Cash' : 'Point',
            errorType: 'NoError',
          };
        }

        if (this.selectedCafeMenuInfo.upperLimitPoint === 0) {
          this.selectNonUsePoint();
        }

        if (res.cafeReservation?.useCafeFlag === false) {
          this.selectNonUsePoint();
        }
      })
      .catch((error: unknown) => {
        if (ApiService.isErrorData(error)) {
          this.commonErrorResponse = error;
        } else {
          this.$router.push({
            name: 'Erro01',
          });
        }
      })
      .finally(() => {
        store.commit('setIsLoading', false);
      });
  }

  doApiFetCafeFellowTravelers(): void {
    const config = OrderApiConfig.newInstance();
    const apiOrder = new DPApi(config);
    store.commit('setIsLoading', true);

    apiOrder
      .getCafeFellowTravelers()
      .then((res) => {
        this.cafeGetResponse = res;

        // restore radio.
        if (res.cafeReservation && res.cafeReservation.cafeMenuNo && res.cafeMenuInfo) {
          const menuNo = res.cafeReservation.cafeMenuNo;
          const cafeMenuInfo = res.cafeMenuInfo.find((info) => info.cafeMenuNo === menuNo);
          if (cafeMenuInfo) {
            this.changeCafeMenuInfo(cafeMenuInfo);
          }
        }

        // init radio if needed.
        if (Object.keys(this.selectedCafeMenuInfo).length === 0 && res.cafeMenuInfo && res.cafeMenuInfo.length > 0) {
          const [first] = res.cafeMenuInfo;
          this.changeCafeMenuInfo(first);
        }

        // restore points.
        if (res.cafeReservation) {
          this.form.usePoint = {
            value: Number(res.cafeReservation.applyValue) ?? 0,
            type: res.cafeReservation.applyUnit === '1' ? 'Cash' : 'Point',
            errorType: 'NoError',
          };
        }

        if (this.selectedCafeMenuInfo.upperLimitPoint === 0) {
          this.selectNonUsePoint();
        }

        if (res.cafeReservation?.useCafeFlag === false) {
          this.selectNonUsePoint();
        }
      })
      .catch((error: unknown) => {
        if (ApiService.isErrorData(error)) {
          this.commonErrorResponse = error;
        } else {
          this.$router.push({
            name: 'Erro01',
          });
        }
      })
      .finally(() => {
        store.commit('setIsLoading', false);
      });
  }

  //* ***********************************************************************************/
  // Methods
  //* ***********************************************************************************/
  clickStepDown(): void {
    if (this.disabledInputUsePoint || Number(this.form.usePoint.value) <= 0) return;

    this.form.usePoint.value = Number(this.form.usePoint.value) - 1;
    this.validateUsePoint(this.form.usePoint.value, this.form.usePoint.type);
  }

  clickStepUp(): void {
    if (this.disabledInputUsePoint) return;

    this.form.usePoint.value = Number(this.form.usePoint.value) + 1;
    this.validateUsePoint(this.form.usePoint.value, this.form.usePoint.type);
  }

  clickCancel(): void {
    this.$emit('close');
  }

  clickSubmitPre(): void {
    if (this.cafeGetResponse.benepoUseFlag !== '0' && this.cafeGetResponse.benepoAmountTotal && this.cafeGetResponse.benepoAmountTotal >= 1) {
      this.showConfirmModal = true;
    } else {
      this.clickSubmit();
    }
  }

  onConfirmSubmit(): void {
    this.showConfirmModal = false;
    this.clickSubmit();
  }

  onConfirmCancel(): void {
    this.showConfirmModal = false;
  }

  clickSubmit(): void {
    const config = OrderApiConfig.newInstance();
    const apiOrder = new DPApi(config);
    store.commit('setIsLoading', true);

    let applyUnit: string | undefined;
    switch (this.form.usePoint.type) {
      case 'Point':
        applyUnit = '0';
        break;
      case 'Cash':
        applyUnit = '1';
        break;
      default:
        applyUnit = undefined;
    }

    const request: PutCafeRequest = {
      jpCoBenefitoneBsdpBffPresentationRequestCafePutRequest: {
        cafeMenuNo: this.selectedCafeMenuInfo.cafeMenuNo,
        cafeMenuBranchNo: this.selectedCafeMenuInfo.cafeMenuBranchNo,
        cafeSubscriptionId: this.selectedCafeMenuInfo.cafeSubscriptionId,
        applyValue: this.form.usePoint.value.toString(),
        applyUnit,
        useCafeFlag: this.useCafeFlag,
      },
    };

    if (this.orderSite === 4) {
      apiOrder
        .putCafeFellowTravelers(request)
        .then(() => {
          store.commit('setIsLoading', false);
          this.$emit('submit');
        })
        .catch((error) => {
          store.commit('setIsLoading', false);
          if (error.status !== 400) {
            this.$router.push({
              name: 'Erro01',
            });
          }
          return error.json();
        })
        .then(() => {
          this.cafeErrorResponse = {};
        });
    } else {
      apiOrder
        .putCafe(request)
        .then(() => {
          store.commit('setIsLoading', false);
          this.$emit('submit');
        })
        .catch((error) => {
          store.commit('setIsLoading', false);
          if (error.status !== 400) {
            this.$router.push({
              name: 'Erro01',
            });
          }
          return error.json();
        })
        .then(() => {
          this.cafeErrorResponse = {};
        });
    }
  }

  changeCafeMenuInfo(newCafeMenuInfo: JpCoBenefitoneBsdpBffPresentationResponseCafeGetResponseCafeMenuInfo): void {
    this.selectedCafeMenuInfo = newCafeMenuInfo;

    this.form = {
      usePoint: {
        value: 1,
        type: 'Point',
        errorType: 'NoError',
      },
      checkedAgree1: !this.showAgree1(newCafeMenuInfo),
      checkedAgree2: !this.showAgree2(newCafeMenuInfo),
    };
    this.useCafeFlag = true;
    this.disabledInputUsePoint = false;
  }

  selectNonUsePoint(): void {
    this.form = {
      usePoint: {
        value: 0,
        type: 'Point',
        errorType: 'NoError',
      },
      checkedAgree1: true,
      checkedAgree2: true,
    };
    this.useCafeFlag = false;
    this.disabledInputUsePoint = true;
  }

  changeUsePointType(): void {
    this.validateUsePoint(this.form.usePoint.value, this.form.usePoint.type);
  }

  changeSpinner(): void {
    this.validateUsePoint(this.form.usePoint.value, this.form.usePoint.type);
  }

  disabledSubmit(): boolean {
    return this.form.usePoint.errorType !== 'NoError' || !this.form.checkedAgree1 || !this.form.checkedAgree2;
  }

  generatePointRateLabel(rounding?: string): string {
    if (!rounding) return '';

    switch (rounding) {
      case '01':
        return '切り捨て';
      case '02':
        return '切り上げ';
      case '03':
        return '四捨五入';
      default:
        return '';
    }
  }

  generateRateForPoint(cafeMenuInfo: JpCoBenefitoneBsdpBffPresentationResponseCafeGetResponseCafeMenuInfo): number {
    if (!this.form.usePoint.value || !cafeMenuInfo.pointRate) return 0;

    const result = this.form.usePoint.value * cafeMenuInfo.pointRate;
    switch (cafeMenuInfo.rounding) {
      case '01':
        return Math.floor(result);

      case '02':
        return Math.ceil(result);

      case '03':
        return Math.round(result);

      default:
        return result;
    }
  }

  showAgree1(cafeMenuInfo: JpCoBenefitoneBsdpBffPresentationResponseCafeGetResponseCafeMenuInfo): boolean {
    return cafeMenuInfo.memberAccompany ?? false;
  }

  showAgree2(cafeMenuInfo: JpCoBenefitoneBsdpBffPresentationResponseCafeGetResponseCafeMenuInfo): boolean {
    // validate.
    if (!cafeMenuInfo.targetUsers || !cafeMenuInfo.targetUsers.restriction) {
      return false;
    }

    return [1, 2, 3].includes(cafeMenuInfo.targetUsers.restriction);
  }

  private validateUsePoint(newValue: number | '', type: UsePointType): void {
    let errorType: UsePointErrorType = 'NoError';

    if (newValue === '') {
      errorType = 'Blank';
    } else if (newValue < 1) {
      errorType = 'NotPositive';
    } else if (newValue.toString().match(/^[0-9]+\.[0-9]+$/)) {
      errorType = 'NotNum';
    } else if (
      type === 'Point' &&
      this.cafeGetResponse.billingAmount !== undefined &&
      this.generateRateForPoint(this.selectedCafeMenuInfo) > this.cafeGetResponse.billingAmount
    ) {
      errorType = 'Over';
    } else if (type === 'Cash' && this.cafeGetResponse.billingAmount !== undefined && newValue > this.cafeGetResponse.billingAmount) {
      errorType = 'Over';
    } else if (
      type === 'Point' &&
      this.selectedCafeMenuInfo.upperLimitPoint !== undefined &&
      (this.selectedCafeMenuInfo.upperLimitPoint === 0 || newValue > this.selectedCafeMenuInfo.upperLimitPoint)
    ) {
      errorType = 'ExceedPoint';
    } else if (type === 'Cash' && this.selectedCafeMenuInfo.upperLimitAmount != null) {
      const pointRate = this.selectedCafeMenuInfo.pointRate ? this.selectedCafeMenuInfo.pointRate : 0;
      if (this.selectedCafeMenuInfo.upperLimitAmount === 0 || newValue * pointRate > this.selectedCafeMenuInfo.upperLimitAmount * pointRate) {
        errorType = 'ExceedCash';
      }
    } else {
      errorType = 'NoError';
    }

    this.form.usePoint.errorType = errorType;
  }
}
